import { useState, use, useMemo, useDeferredValue } from 'react';
import { DBContext } from '@/lib/context';
import { collection, getDocs } from 'firebase/firestore';
import { DBCollection } from '@/lib/utils/constants-shared';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Search, Library, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, Download, BarChart3, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';

interface SourceSummary {
    citation: string;
    searchTerms: string;
    count: number;
    collections: Set<string>;
    collectionsArray?: string[]; // Pre-computed for faster filtering
}

export default function AdminBackgroundSources() {
    const db = use(DBContext);
    const [isLoading, setIsLoading] = useState(false);
    const [sources, setSources] = useState<SourceSummary[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const deferredSearchQuery = useDeferredValue(searchQuery);
    const [queryStatus, setQueryStatus] = useState<string>('');
    const [lastScanned, setLastScanned] = useState<Date | null>(null);
    const [totalRefs, setTotalRefs] = useState(0);
    const [sortField, setSortField] = useState<'citation' | 'count'>('count');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    const querySources = async () => {
        setIsLoading(true);
        setSources([]);
        setTotalRefs(0);
        const sourceMap = new Map<string, SourceSummary>();
        let runningTotalRefs = 0;

        const collectionsToQuery = [
            DBCollection.cultureByPassage,
            DBCollection.cultureByBook,
            DBCollection.archaeologyByPassage,
            DBCollection.archaeologyByBook,
            DBCollection.historicalEventsByPassage,
            DBCollection.historicalEventsByBook,
            DBCollection.historicalEvents,
            DBCollection.placesByPassage,
            DBCollection.places,
            DBCollection.literaryFeaturesByPassage,
            DBCollection.literaryFeaturesByBook,
            DBCollection.peopleByPassage,
            DBCollection.people,
            DBCollection.studyGuideByPassage,
            DBCollection.guidedStudyByPassage
        ];

        try {
            for (let i = 0; i < collectionsToQuery.length; i++) {
                const coll = collectionsToQuery[i];
                setQueryStatus(`Querying ${coll}... (${i + 1}/${collectionsToQuery.length})`);

                const snapshot = await getDocs(collection(db, coll));
                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    runningTotalRefs += extractSourcesFromObject(data, coll, sourceMap);
                });
            }

            const sortedSources = Array.from(sourceMap.values()).sort((a, b) => b.count - a.count);
            // Pre-compute collectionsArray for faster filtering
            const sourcesWithArrays = sortedSources.map(s => ({
                ...s,
                collectionsArray: Array.from(s.collections)
            }));
            setSources(sourcesWithArrays);
            setTotalRefs(runningTotalRefs);
            setLastScanned(new Date());
            setQueryStatus(`Completed! Found ${sortedSources.length} unique sources.`);
        } catch (error) {
            console.error("Error querying sources:", error);
            setQueryStatus("Error occurred during query. Check console.");
        } finally {
            setIsLoading(false);
        }
    };

    const extractSourcesFromObject = (obj: any, collName: string, map: Map<string, SourceSummary>): number => {
        if (!obj || typeof obj !== 'object') return 0;

        let foundCount = 0;

        if (Array.isArray(obj)) {
            obj.forEach(item => {
                foundCount += extractSourcesFromObject(item, collName, map);
            });
            return foundCount;
        }

        // Check if this object is a Source
        if (obj.citation && typeof obj.citation === 'string' && obj.searchTerms !== undefined) {
            const citation = obj.citation.trim();
            if (citation) {
                const existing = map.get(citation);
                if (existing) {
                    existing.count++;
                    existing.collections.add(collName);
                } else {
                    map.set(citation, {
                        citation,
                        searchTerms: obj.searchTerms || '',
                        count: 1,
                        collections: new Set([collName])
                    });
                }
                // We found 1 source instance
                return 1;
            }
        }

        // Recurse into all properties
        for (const key in obj) {
            const val = obj[key];
            if (val && typeof val === 'object') {
                foundCount += extractSourcesFromObject(val, collName, map);
            }
        }

        return foundCount;
    };

    const downloadCSV = () => {
        if (sources.length === 0) return;

        const headers = ["Citation", "Reference Count", "Collections", "Search Terms"];
        const rows = sources.map(s => [
            `"${s.citation.replace(/"/g, '""')}"`,
            s.count,
            `"${Array.from(s.collections).join(', ')}"`,
            `"${s.searchTerms.replace(/"/g, '""')}"`
        ]);

        const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `bible-glen-sources-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSort = (field: 'citation' | 'count') => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const filteredSources = useMemo(() => {
        const filtered = sources.filter(s =>
            s.citation.toLowerCase().includes(deferredSearchQuery.toLowerCase()) ||
            (s.collectionsArray || Array.from(s.collections)).some(c => c.toLowerCase().includes(deferredSearchQuery.toLowerCase()))
        );

        return [...filtered].sort((a, b) => {
            const multiplier = sortDirection === 'asc' ? 1 : -1;
            if (sortField === 'count') {
                return (a.count - b.count) * multiplier;
            }
            return a.citation.localeCompare(b.citation) * multiplier;
        });
    }, [sources, deferredSearchQuery, sortField, sortDirection]);

    const SortIcon = ({ field }: { field: 'citation' | 'count' }) => {
        if (sortField !== field) return <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />;
        return sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4" /> : <ArrowDown className="ml-2 h-4 w-4" />;
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <div className="space-y-1">
                        <CardTitle className="text-2xl flex items-center gap-2">
                            <Library className="h-6 w-6" />
                            Sources Summary
                        </CardTitle>
                        <CardDescription>
                            Analyze citations and credits used across all background and reference datasets.
                        </CardDescription>
                    </div>
                    <Button onClick={querySources} disabled={isLoading} size="lg" className="px-8">
                        {isLoading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Search className="mr-2 h-5 w-5" />}
                        Scan All Datasets
                    </Button>
                </CardHeader>
                <CardContent className="pt-6">
                    {sources.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                            <div className="bg-muted/30 p-4 rounded-lg border flex items-center gap-4">
                                <div className="p-3 bg-blue-500/10 rounded-full">
                                    <Library className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider">Unique Sources</p>
                                    <p className="text-2xl font-bold">{sources.length.toLocaleString()}</p>
                                </div>
                            </div>
                            <div className="bg-muted/30 p-4 rounded-lg border flex items-center gap-4">
                                <div className="p-3 bg-green-500/10 rounded-full">
                                    <BarChart3 className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider">Total Citations</p>
                                    <p className="text-2xl font-bold">{totalRefs.toLocaleString()}</p>
                                </div>
                            </div>
                            <div className="bg-muted/30 p-4 rounded-lg border flex items-center gap-4">
                                <div className="p-3 bg-orange-500/10 rounded-full">
                                    <Clock className="h-5 w-5 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground uppercase font-bold tracking-wider">Last Scanned</p>
                                    <p className="text-sm font-medium">{lastScanned ? lastScanned.toLocaleTimeString() : 'N/A'}</p>
                                </div>
                            </div>
                            <div className="flex items-center justify-end">
                                <Button variant="outline" onClick={downloadCSV} className="w-full h-full">
                                    <Download className="mr-2 h-4 w-4" />
                                    Export CSV
                                </Button>
                            </div>
                        </div>
                    )}

                    {queryStatus && (
                        <div className={`text-sm mb-4 p-3 rounded-md border ${isLoading ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800' : 'bg-muted border-border'}`}>
                            {queryStatus}
                        </div>
                    )}

                    <div className="flex items-center gap-4 mb-6">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by citation or collection..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        {sources.length > 0 && (
                            <div className="text-sm text-muted-foreground">
                                Showing {filteredSources.length} of {sources.length} sources
                            </div>
                        )}
                    </div>

                    {sources.length > 0 ? (
                        <div className="border rounded-md overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50">
                                        <TableHead
                                            className="w-[400px] cursor-pointer hover:bg-muted transition-colors"
                                            onClick={() => handleSort('citation')}
                                        >
                                            <div className="flex items-center">
                                                Source Citation
                                                <SortIcon field="citation" />
                                            </div>
                                        </TableHead>
                                        <TableHead
                                            className="text-center w-24 cursor-pointer hover:bg-muted transition-colors"
                                            onClick={() => handleSort('count')}
                                        >
                                            <div className="flex items-center justify-center">
                                                Refs
                                                <SortIcon field="count" />
                                            </div>
                                        </TableHead>
                                        <TableHead>Found In Collections</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredSources.map((s, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell className="font-medium whitespace-normal py-4">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-base text-foreground leading-snug">{s.citation}</span>
                                                    {s.searchTerms && (
                                                        <a
                                                            href={`https://www.google.com/search?q=${encodeURIComponent(s.searchTerms)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-xs text-blue-600 hover:orange-600 hover:underline flex items-center gap-1 w-fit opacity-70"
                                                        >
                                                            <ExternalLink className="h-3 w-3" />
                                                            Research Source
                                                        </a>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Badge variant="outline" className="text-base font-bold px-3 py-1">
                                                    {s.count.toLocaleString()}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="whitespace-normal py-4">
                                                <div className="flex flex-wrap gap-1.5 max-w-md">
                                                    {Array.from(s.collections).map(coll => (
                                                        <Badge key={coll} variant="secondary" className="text-[10px] uppercase font-bold tracking-tight py-0.5 px-2 opacity-80">
                                                            {coll}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-muted/20 border-2 border-dashed rounded-lg">
                            <Library className="mx-auto h-16 w-16 text-muted-foreground/20 mb-4" />
                            <h3 className="text-lg font-medium text-muted-foreground">No data scanned</h3>
                            <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">
                                Click the "Scan All Datasets" button to traverse through all background data and compile a list of source citations.
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
